<template>
  <div class="card bg-light">
    <div class="card-body">
      <h6 class="card-title">Source Details</h6>
      <div class="mb-3">
        <label class="form-label small text-muted">Acquisition Date</label>
        <utc-date-input v-model="acquisitionDate" class="form-control form-control-sm" />
      </div>
      <div class="d-flex align-items-center mb-3">
        <label class="form-label small text-muted">Price Paid</label>
        <div class="ms-auto">
          <inline-edit :current-value="localSourcePrice" @on-save="updateSourcePrice">$</inline-edit>
        </div>
      </div>
      <div>
        <source-input v-model="sourceData" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue'
import { useStore } from 'vuex'
import UtcDateInput from '@/app/components/Utils/UtcDateInput.vue'
import InlineEdit from '@/app/components/Utils/InlineEdit.vue'
import SourceInput from '@/app/components/inventory/SourceInput.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && typeof value.id !== 'undefined'
    }
  }
})

const emit = defineEmits(['update:source-price', 'update:acquisition-date', 'update:source-data'])
const store = useStore()

const localSourcePrice = ref(props.item.source_price)
const acquisitionDate = ref(props.item.acquisition_date || null)
const sourceData = ref({
  source_category_id: props.item.source_category_id || null,
  source_location_id: props.item.source_location_id || null
})

let acquisitionDateTimeout
let sourceDataTimeout

function updateSourcePrice(newPrice) {
  const parsedPrice = parseFloat(newPrice)
  store.dispatch('eshop/updateInventorySourcePrice', {
    ...props.item,
    source_price: parsedPrice
  }).then(() => {
    localSourcePrice.value = parsedPrice
    emit('update:source-price', parsedPrice)
  }).catch(error => console.log(error))
}

function updateAcquisitionDate(newDate) {
  store.dispatch('eshop/updateInventoryAcquisitionDate', {
    ...props.item,
    acquisition_date: newDate
  }).then(() => {
    emit('update:acquisition-date', newDate)
  }).catch(error => console.log(error))
}

async function updateSourceData(newSourceData) {
  const item = { ...props.item }

  try {
    // Update sequentially to avoid setInventoryItem commits racing and
    // resetting props.item mid-flight, which would re-trigger the watcher
    if (newSourceData.source_category_id !== item.source_category_id) {
      await store.dispatch('eshop/updateInventorySourceCategory', {
        ...item,
        source_category_id: parseInt(newSourceData.source_category_id) || null
      })
    }

    if (newSourceData.source_location_id !== item.source_location_id) {
      await store.dispatch('eshop/updateInventorySourceLocation', {
        ...item,
        source_location_id: parseInt(newSourceData.source_location_id) || null
      })
    }

    emit('update:source-data', newSourceData)
  } catch (error) {
    console.log(error)
  }
}

// Watch for acquisition date changes with debounce
watch(() => acquisitionDate.value, (newDate) => {
  if (newDate !== props.item.acquisition_date) {
    clearTimeout(acquisitionDateTimeout)
    acquisitionDateTimeout = setTimeout(() => {
      updateAcquisitionDate(newDate)
    }, 500)
  }
})

// Watch for source data changes with debounce
watch(() => sourceData.value, (newSourceData) => {
  const categoryChanged = newSourceData.source_category_id !== props.item.source_category_id
  const locationChanged = newSourceData.source_location_id !== props.item.source_location_id

  if (categoryChanged || locationChanged) {
    clearTimeout(sourceDataTimeout)
    sourceDataTimeout = setTimeout(() => {
      updateSourceData(newSourceData)
    }, 500)
  }
}, { deep: true })

// Reset local state when item prop changes (only if values actually differ)
watch(() => props.item, (newItem) => {
  if (localSourcePrice.value !== newItem.source_price) {
    localSourcePrice.value = newItem.source_price
  }
  const newDate = newItem.acquisition_date || null
  if (acquisitionDate.value !== newDate) {
    acquisitionDate.value = newDate
  }
  const newCategoryId = newItem.source_category_id || null
  const newLocationId = newItem.source_location_id || null
  if (sourceData.value.source_category_id !== newCategoryId || sourceData.value.source_location_id !== newLocationId) {
    sourceData.value = { source_category_id: newCategoryId, source_location_id: newLocationId }
  }
}, { deep: true })
</script>
